import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DocumentArrowDownIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { API_URL } from '@/lib/api';

interface ShareInfo {
  name: string;
  size?: number | null;
  mimeType?: string | null;
  isPasswordProtected?: boolean;
  allowDownload?: boolean;
  expiresAt?: string | null;
  ownerName?: string | null;
}

/**
 * The public face of a share link: no account, no navigation — the recipient
 * opens the link, unlocks it if it is protected, downloads the file. The vault
 * validates the link server-side (revoked, expired, password, download limit).
 */
export function ShareLandingPage() {
  const { shortId } = useParams<{ shortId: string }>();
  const { t } = useTranslation();
  const [info, setInfo] = useState<ShareInfo | null>(null);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const base = `${API_URL}/api/shares/${encodeURIComponent(shortId ?? '')}`;

  const load = useCallback(
    async (withPassword?: string) => {
      const query = withPassword ? `?password=${encodeURIComponent(withPassword)}` : '';
      const res = await fetch(`${base}${query}`);
      const data = (await res.json().catch(() => ({}))) as ShareInfo & {
        error?: string;
        passwordRequired?: boolean;
      };
      if (res.ok) {
        setInfo(data);
        setError(null);
        return;
      }
      if (data.passwordRequired) {
        setNeedsPassword(true);
      }
      throw new Error(data.error || `Request failed with ${res.status}`);
    },
    [base],
  );

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [load]);

  const unlock = async () => {
    setError(null);
    try {
      await load(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const download = async () => {
    if (!info) return;
    setDownloading(true);
    setError(null);
    try {
      const query = password ? `?password=${encodeURIComponent(password)}` : '';
      const res = await fetch(`${base}/download${query}`);
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || `Download failed with ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = info.name;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-xl border border-border-color bg-card p-8 shadow-sm">
        <DocumentArrowDownIcon className="h-10 w-10 text-accent" />
        <h1 className="max-w-full break-all text-center text-xl font-semibold text-foreground">
          {info?.name ?? t('shareLanding.title')}
        </h1>
        {info?.ownerName && (
          <p className="text-sm text-muted-foreground">{t('shareLanding.from', { name: info.ownerName })}</p>
        )}

        {error ? (
          <p className="text-center text-sm text-destructive">{error}</p>
        ) : needsPassword && !info ? (
          <div className="flex w-full flex-col gap-3">
            <p className="flex items-center justify-center gap-2 text-center text-sm text-muted-foreground">
              <LockClosedIcon className="h-4 w-4 flex-shrink-0" />
              {t('shareLanding.passwordPrompt')}
            </p>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void unlock();
              }}
              placeholder={t('shareLanding.password')}
              className="w-full rounded-md border border-border-color bg-background px-2 py-1.5 text-sm text-foreground"
            />
            <button
              type="button"
              onClick={() => void unlock()}
              className="whitespace-nowrap rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90"
            >
              {t('shareLanding.unlock')}
            </button>
          </div>
        ) : info ? (
          info.allowDownload === false ? (
            <p className="text-center text-sm text-muted-foreground">{t('shareLanding.downloadsDisabled')}</p>
          ) : (
            <button
              type="button"
              disabled={downloading}
              onClick={() => void download()}
              className="whitespace-nowrap rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {downloading ? t('common.working') : t('shareLanding.download')}
            </button>
          )
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">{t('shareLanding.securedBy')}</p>
    </div>
  );
}
